import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, CheckCircle2, Eye, ShieldCheck, ArrowRight, RotateCcw } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementPopup from '../components/AnnouncementPopup';
import { trackPageView, trackSlotImpressions, trackProductClick } from '../utils/analytics';
import { API_URL } from '../utils/apiUrl';
import { canRenderPublicVideo } from '../utils/videoUrlValidation';

/* ── Product Card (Premium) ── */
const ProductCard = ({ product, slug, onClick }) => {
  const isLow = product.score < 7;
  const href = slug ? `/v/${slug}` : `/products/${product.id}`;
  const hasDetail = slug || product.id;

  const inner = (
    <>
      {/* Media — 4:3 ratio */}
      <div className="relative w-full" style={{ aspectRatio: '4/3' }}>
        {product.image_url ? (
          <img src={product.image_url} alt={product.name || 'Ürün görseli'} width={400} height={300}
            className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        ) : product.video_url && product.video_type === 'embed' && product.video_url.includes('youtube') ? (
          <img src={`https://img.youtube.com/vi/${product.video_url.split('/embed/')[1]?.split('?')[0] || ''}/hqdefault.jpg`}
            alt={product.name || 'Ürün görseli'} width={400} height={300}
            className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #d4d4d4 0%, #bfbfbf 100%)', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)' }}>
            <div className="text-center">
              <ShieldCheck className="w-8 h-8 mx-auto mb-1" style={{ color: 'rgba(255,255,255,0.6)' }} />
              <span className="text-[10px] font-bold" style={{ color: 'rgba(255,255,255,0.7)' }}>TestettiK®</span>
            </div>
          </div>
        )}
        {/* Score badge */}
        <div className="absolute top-2.5 right-2.5 px-3 py-1.5 rounded-md shadow-md bg-[#1f3d2b]"
          aria-label={`Puan: ${product.score?.toFixed(1)}`}>
          <span className="text-white font-bold text-base">{product.score?.toFixed(1)}</span>
          <span className="text-white/50 text-[10px]"> /10</span>
        </div>
        {/* TT-VIDEO-001/002: lightweight "Video Kanıt Var" badge — new whitelisted metadata only */}
        {canRenderPublicVideo(product.video) && (
          <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-white/95 shadow-md"
            style={{ color: 'var(--green-main)' }}
            data-testid={`video-badge-${product.id}`}
            aria-label="Video kanıt var">
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
            Video Kanıt
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-4">
        <span className={`inline-block px-2.5 py-1 text-[11px] font-semibold rounded mb-2 ${isLow ? 'bg-[#7A1F1F] text-white' : 'bg-[#1f3d2b] text-white'}`}>
          {isLow ? 'Beklentiyi karşılamadı' : 'Test Onaylandı'}
        </span>
        <h4 className="text-sm font-bold leading-snug mb-0.5 line-clamp-2" style={{ color: 'var(--text-primary)' }}>{product.name}</h4>
        <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>{product.brand}</p>
        {hasDetail ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1f3d2b]">
            Detayları Gör <ArrowRight className="w-3 h-3" />
          </span>
        ) : (
          <span className="text-xs text-neutral-400">Detay yakında</span>
        )}
      </div>
    </>
  );

  if (hasDetail) {
    return (
      <Link to={href} onClick={onClick}
        className="overflow-hidden group hover:-translate-y-0.5 transition-all duration-200 min-w-[250px] snap-start flex-shrink-0 md:min-w-0 md:flex-shrink md:snap-align-none focus-visible:ring-2 focus-visible:ring-offset-2 outline-none rounded-xl border"
        style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }}
        data-testid={`product-card-${product.id}`} aria-label={`${product.name} — ${product.score?.toFixed(1)}/10`}>
        {inner}
      </Link>
    );
  }

  return (
    <div className="overflow-hidden opacity-70 min-w-[250px] snap-start flex-shrink-0 md:min-w-0 md:flex-shrink md:snap-align-none rounded-xl border"
      style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }}
      data-testid={`product-card-${product.id}`} aria-label={`${product.name} — detay yakında`}>
      {inner}
    </div>
  );
};

/* ── Skeleton Card ── */
const SkeletonCard = () => (
  <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden min-w-[250px] flex-shrink-0 md:min-w-0 shadow-sm">
    <div
      className="w-full animate-pulse"
      style={{
        aspectRatio: '4/3',
        background: 'linear-gradient(135deg, #e5e7eb 0%, #d4d4d8 50%, #e5e7eb 100%)',
      }}
    />
    <div className="p-4 space-y-2">
      <div className="h-3 bg-neutral-300 rounded w-1/3 animate-pulse" />
      <div className="h-4 bg-neutral-300 rounded w-3/4 animate-pulse" />
      <div className="h-3 bg-neutral-200 rounded w-1/2 animate-pulse" />
    </div>
  </div>
);

/* ── Main ── */
const HomePage = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [slotProducts, setSlotProducts] = useState([]); // Premium slot assignments + fallbacks (from /api/homepage/active)
  const [slugMap, setSlugMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [stats, setStats] = useState({ total: 0, approved: 0 });
  const productsRef = useRef(null);
  const howRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    let timer = setTimeout(() => setTimedOut(true), 3000);

    const fetchData = async () => {
      try {
        const [slotRes, allRes] = await Promise.all([
          axios.get(`${API_URL}/api/homepage/active`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/products`).catch(() => ({ data: [] })),
        ]);
        clearTimeout(timer);
        const products = allRes.data;
        setAllProducts(products);
        setSlotProducts(slotRes.data || []);
        setStats({
          total: products.length,
          approved: products.filter(p => p.score >= 7 && p.published).length,
        });
        trackPageView('homepage_view');
        trackSlotImpressions(slotRes.data);

        // Fetch slugs for any product appearing in slots or score >= 7
        const slotIds = (slotRes.data || []).map(p => p.id);
        const highIds = products.filter(p => p.score >= 7).map(p => p.id);
        const uniqueIds = [...new Set([...slotIds, ...highIds])];
        const slugEntries = await Promise.all(
          uniqueIds.map(id =>
            axios.get(`${API_URL}/api/verification-links/${id}`).then(r => r.data.exists ? [id, r.data.slug] : null).catch(() => null)
          )
        );
        const map = {};
        slugEntries.filter(Boolean).forEach(([id, slug]) => { map[id] = slug; });
        setSlugMap(map);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
    return () => clearTimeout(timer);
  }, []);

  // ── PREMIUM SLOT-ASSIGNED PRODUCTS (admin-curated) ──
  // Backend returns 15 slots sorted; we deduplicate by product id (preserve slot order)
  // and only display products that are explicitly premium-assigned (not random fallback)
  const premiumAssigned = [];
  const seenInSlots = new Set();
  for (const sp of slotProducts) {
    if (!sp || !sp.id || seenInSlots.has(sp.id)) continue;
    if (sp.is_premium === true) {
      premiumAssigned.push(sp);
      seenInSlots.add(sp.id);
    }
  }
  // Cap displayed premium products at 6 for the homepage section
  const premiumDisplay = premiumAssigned.slice(0, 6);
  const premiumIds = new Set(premiumDisplay.map(p => p.id));

  // High score (dedup against premium, max 6)
  const highScore = [...allProducts]
    .filter(p => p.score >= 7 && p.published && !premiumIds.has(p.id))
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);

  // Latest (dedup against premium + highScore, max 6)
  const usedIds = new Set([...premiumIds, ...highScore.map(p => p.id)]);
  const latest = [...allProducts]
    .filter(p => p.published && !usedIds.has(p.id))
    .sort((a, b) => (b.created_at || b.updated_at || '').localeCompare(a.created_at || a.updated_at || ''))
    .slice(0, 6);

  const showLatest = latest.length >= 3;

  // Categories
  const categories = [...new Set(allProducts.filter(p => p.category).map(p => p.category))];

  const scrollTo = (ref) => (e) => {
    e.preventDefault();
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const showSkeleton = loading && !timedOut;
  const showEmpty = !loading && allProducts.length === 0;

  return (
    <div className="min-h-screen bg-white" data-testid="homepage">
      <Header />
      <AnnouncementPopup />

      {/* ═══ HERO — #FFF ═══ */}
      <section className="px-5 md:px-6 lg:px-12 pt-12 pb-12 md:pt-20 md:pb-20 border-b" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid="hero-section">
        <div className="max-w-[1200px] mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.12] mb-4 max-w-3xl mx-auto" style={{ color: 'var(--text-primary)' }} data-testid="hero-h1">
            Onayladığımız Ürünleri Güvenle Alabilirsiniz
          </h1>
          <p className="text-base sm:text-lg font-semibold mb-2 max-w-xl mx-auto leading-relaxed" style={{ color: 'var(--text-secondary-strong)' }} data-testid="hero-subtext">
            Kargo geldiğinde kötü sürpriz yaşamayın.<br className="sm:hidden" /> Biz sizin için test ettik.
          </p>
          <p className="text-sm font-normal mb-8" style={{ color: 'var(--text-secondary)' }} data-testid="hero-system-line">
            TestettiK®, ürünleri bağımsız test eder ve sonucu doğrudan yayınlar.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#products" onClick={scrollTo(productsRef)}
              className="bg-[#1C3B2A] text-white px-8 py-3.5 text-sm font-semibold tracking-wide rounded-lg hover:bg-[#152e21] transition text-center focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1C3B2A] outline-none"
              data-testid="hero-cta-primary" role="button" tabIndex={0}>
              Test Edilmiş Ürünleri Gör
            </a>
            <a href="#nasil" onClick={scrollTo(howRef)}
              className="border-2 border-neutral-300 text-neutral-700 px-8 py-3.5 text-sm font-semibold tracking-wide rounded-lg hover:border-neutral-400 hover:bg-neutral-50 transition text-center focus-visible:ring-2 focus-visible:ring-offset-2 outline-none"
              data-testid="hero-cta-secondary" role="button" tabIndex={0}>
              Sistem Nasıl Çalışır
            </a>
          </div>
        </div>
      </section>

      {/* ═══ TRUST PROOF — #FAFAFA ═══ */}
      {/* ═══ TT-HOMEPAGE-001 · 4-CARD DARK GREEN FEATURE BAND ═══ */}
      <section
        className="px-5 md:px-8 lg:px-12 py-10 md:py-12 lg:py-16"
        style={{ background: '#1C3B2A' }}
        data-testid="trust-proof"
      >
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {[
            { Icon: Eye,         title: 'Satın Almadan Önce Gerçek Halini Gör',  desc: 'Ürünün gerçek görünümünü, kalitesini ve performansını satın almadan önce bağımsız gözle incele.', tid: 'trust-item-see-before-buy' },
            { Icon: ShieldCheck, title: 'Test Sonucunu Doğrudan İncele',          desc: 'Puan, video ve kriterler doğrudan yayınlanır — filtrelenmez, değiştirilmez.', tid: 'trust-item-direct-review' },
            { Icon: CheckCircle2,title: 'Hayal Kırıklığı Yaşama',                 desc: 'Bağımsız test sonuçlarıyla aldığın ürün tam olarak beklediğin ürün olur.', tid: 'trust-item-no-disappointment' },
            { Icon: RotateCcw,   title: 'İade Süreçleriyle Uğraşma',              desc: 'Doğru ürünü ilk seferinde al. İade, şikâyet ve zaman kaybından kaçın.', tid: 'trust-item-avoid-returns' },
          ].map(({ Icon, title, desc, tid }, i, arr) => (
            <div
              key={tid}
              data-testid={tid}
              className={`flex flex-col items-center text-center px-6 py-6 md:py-4 ${
                i < arr.length - 1 ? 'lg:border-r lg:border-white/20' : ''
              }`}
            >
              <Icon
                width={40}
                height={40}
                strokeWidth={1.5}
                color="#FFFFFF"
                aria-hidden="true"
                className="mb-3"
              />
              <h3 className="text-white font-semibold text-[16px] leading-snug mb-2">{title}</h3>
              <p
                className="text-white text-[14px] leading-snug max-w-[280px] md:max-w-[220px] lg:max-w-[180px]"
                style={{ opacity: 0.82 }}
              >
                {desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ SOCIAL PROOF — min-height fixed ═══ */}
      <section className="px-5 md:px-6 lg:px-12 py-12 md:py-20 border-t" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)', minHeight: 100 }} data-testid="social-proof">
        <div className={`max-w-[1200px] mx-auto flex justify-center gap-10 ${stats.total === 0 ? 'invisible' : ''}`}>
          <div className="text-center">
            <p className="text-3xl font-black text-[#111]" data-testid="stat-total">{stats.total}+</p>
            <p className="text-xs text-neutral-500 font-medium mt-1">Ürün Test Edildi</p>
          </div>
          <div className="w-px bg-neutral-200" />
          <div className="text-center">
            <p className="text-3xl font-black text-[#1C3B2A]" data-testid="stat-approved">{stats.approved}+</p>
            <p className="text-xs text-neutral-500 font-medium mt-1">Ürün Onaylandı</p>
          </div>
        </div>
      </section>

      {/* ═══ ÖNE ÇIKAN ÜRÜNLER (Premium Slot Assignments) — #FFF ═══ */}
      {premiumDisplay.length > 0 && (
        <section className="px-5 md:px-6 lg:px-12 py-12 md:py-20 border-t" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid="premium-section">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg md:text-xl font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>Öne Çıkan Ürünler</h2>
                <p className="text-xs text-neutral-500 mt-1">TestettiK® tarafından seçilmiş, doğrulanmış ürünler</p>
              </div>
              <Link to="/products" className="text-xs font-bold text-[#1C3B2A] hover:underline flex items-center gap-1" data-testid="view-all-premium" tabIndex={0}>
                Tümü <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto snap-x pb-3 md:hidden scrollbar-hide pr-6" data-testid="premium-scroll">
              {premiumDisplay.map(p => <ProductCard key={p.id} product={p} slug={slugMap[p.id]} onClick={() => trackProductClick(p, p.homepage_slot)} />)}
            </div>
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="premium-grid">
              {premiumDisplay.map(p => <ProductCard key={p.id} product={p} slug={slugMap[p.id]} onClick={() => trackProductClick(p, p.homepage_slot)} />)}
            </div>
          </div>
        </section>
      )}

      {/* ═══ EN YÜKSEK PUANLI — #FFF ═══ */}
      <section ref={productsRef} id="products" className="px-5 md:px-6 lg:px-12 py-12 md:py-20 border-t" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid="high-score-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg md:text-xl font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>En Yüksek Puanlı Ürünler</h2>
            <Link to="/products" className="text-xs font-bold text-[#1C3B2A] hover:underline flex items-center gap-1 focus-visible:underline outline-none" data-testid="view-all-high" tabIndex={0}>
              Tümü <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          {showSkeleton ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
            </div>
          ) : showEmpty ? (
            <div className="text-center py-10">
              <p className="text-sm text-neutral-400 mb-4">Henüz test edilmiş ürün bulunmuyor</p>
              <Link to="/products" className="text-sm font-bold text-[#1C3B2A] hover:underline">Tüm ürünleri incele</Link>
            </div>
          ) : highScore.length > 0 ? (
            <>
              <div className="flex gap-4 overflow-x-auto snap-x pb-3 md:hidden scrollbar-hide pr-6" data-testid="high-score-scroll">
                {highScore.map(p => <ProductCard key={p.id} product={p} slug={slugMap[p.id]} onClick={() => trackProductClick(p, p.homepage_slot)} />)}
              </div>
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="high-score-grid">
                {highScore.map(p => <ProductCard key={p.id} product={p} slug={slugMap[p.id]} onClick={() => trackProductClick(p, p.homepage_slot)} />)}
              </div>
            </>
          ) : (
            <p className="text-sm text-neutral-400 py-6 text-center">Henüz yüksek puanlı ürün bulunmuyor</p>
          )}
          <p className="text-[11px] text-neutral-400 mt-4 text-center">Fiyat-performans değerlendirmesi test puanına dahildir.</p>
        </div>
      </section>

      {/* ═══ SON EKLENEN — #FAFAFA ═══ */}
      {showLatest && (
        <section className="px-5 md:px-6 lg:px-12 py-12 md:py-20 border-t" style={{ background: 'var(--bg-section)', borderColor: 'var(--border-soft)' }} data-testid="latest-section">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>Son Eklenen Ürünler</h2>
              <Link to="/products" className="text-xs font-bold text-[#1C3B2A] hover:underline flex items-center gap-1">
                Tümü <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto snap-x pb-3 md:hidden scrollbar-hide pr-6">
              {latest.map(p => <ProductCard key={p.id} product={p} slug={slugMap[p.id]} onClick={() => trackProductClick(p, p.homepage_slot)} />)}
            </div>
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {latest.map(p => <ProductCard key={p.id} product={p} slug={slugMap[p.id]} onClick={() => trackProductClick(p, p.homepage_slot)} />)}
            </div>
          </div>
        </section>
      )}

      {/* ═══ KATEGORİLER — Premium chip grid ═══ */}
      <section className="px-5 md:px-6 lg:px-12 py-12 md:py-20 border-t" style={{ background: 'var(--bg-soft)', borderColor: 'var(--border-soft)' }} data-testid="categories-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-lg md:text-xl font-black tracking-tight mb-1" style={{ color: 'var(--text-primary)' }}>Kategoriler</h2>
            <p className="text-[13px] font-medium" style={{ color: 'var(--text-secondary)' }}>Test edilmiş ürünleri kategorilere göre inceleyin</p>
          </div>

          {/* TT-HOMEPAGE-002 — Deterministic 5-category list in exact order. Categories
              that exist in live API data still route to /products?category=<liveName>
              (case-insensitive lookup) so filter behavior is preserved. Others route to
              /coming-soon. Only "2. El Araç" carries the ÇOK YAKINDA badge. */}
          <div className="flex flex-wrap justify-center gap-2.5 md:gap-3" data-testid="category-chip-list">
            {[
              { label: 'Giyim',      showBadge: false },
              { label: 'Kozmetik',   showBadge: false },
              { label: 'Ayakkabı',   showBadge: false },
              { label: 'Elektronik', showBadge: false },
              { label: 'Aksesuar',   showBadge: false },
              { label: '2. El Araç', showBadge: true  },
            ].map(({ label, showBadge }) => {
              const liveMatch = categories.find(c => c.toLowerCase() === label.toLowerCase());
              const isLive = !!liveMatch && !showBadge;
              const to = isLive
                ? `/products?category=${encodeURIComponent(liveMatch)}`
                : '/coming-soon';
              return (
                <Link
                  key={label}
                  to={to}
                  className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold whitespace-nowrap border transition-all focus-visible:ring-2 focus-visible:ring-offset-2 outline-none hover:-translate-y-0.5"
                  style={
                    isLive
                      ? { background: 'var(--bg-main)', borderColor: 'var(--border-soft)', color: 'var(--text-secondary-strong)' }
                      : { background: '#FAFAF7', borderColor: showBadge ? '#C7A15A' : 'var(--border-soft)', color: 'var(--text-secondary-strong)' }
                  }
                  onMouseEnter={e => {
                    if (isLive) {
                      e.currentTarget.style.borderColor = 'var(--green-main)';
                      e.currentTarget.style.color = 'var(--green-main)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(31,61,43,0.08)';
                    } else {
                      e.currentTarget.style.borderColor = showBadge ? '#B08D48' : '#B8B8B8';
                      e.currentTarget.style.background = '#FFF';
                    }
                  }}
                  onMouseLeave={e => {
                    if (isLive) {
                      e.currentTarget.style.borderColor = 'var(--border-soft)';
                      e.currentTarget.style.color = 'var(--text-secondary-strong)';
                      e.currentTarget.style.boxShadow = 'none';
                    } else {
                      e.currentTarget.style.borderColor = showBadge ? '#C7A15A' : 'var(--border-soft)';
                      e.currentTarget.style.background = '#FAFAF7';
                    }
                  }}
                  data-testid={isLive ? `cat-${liveMatch}` : `cat-coming-soon-${label}`}
                  tabIndex={0}
                  aria-label={showBadge ? `${label} — Çok Yakında` : `Kategori: ${label}`}
                >
                  <span>{label}</span>
                  {showBadge && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider"
                      style={{
                        background: 'linear-gradient(135deg, #C7A15A 0%, #A87F35 100%)',
                        color: '#FFFFFF',
                        letterSpacing: '0.06em',
                      }}
                      data-testid={`badge-coming-soon-${label}`}
                    >
                      ÇOK YAKINDA
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-6">
            <Link to="/products" className="text-xs font-bold hover:underline inline-flex items-center gap-1" style={{ color: 'var(--green-main)' }}>
              Tüm Test Edilmiş Ürünler <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ NASIL ÇALIŞIR — Structured 3-step layout ═══ */}
      <section ref={howRef} id="nasil" className="px-5 md:px-6 lg:px-12 py-12 md:py-20 border-t" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid="how-it-works">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider mb-3" style={{ background: 'var(--bg-accent-soft)', color: 'var(--green-main)', letterSpacing: '0.08em' }}>SÜREÇ</span>
            <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-2" style={{ color: 'var(--text-primary)' }}>Nasıl Çalışır?</h2>
            <p className="text-sm font-medium max-w-lg mx-auto" style={{ color: 'var(--text-secondary)' }}>Her ürün aynı bağımsız süreçten geçer. Kararı siz verirsiniz.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              { n: '01', t: 'Bağımsız Test', d: 'Ürünler markadan bağımsız, sabit kriterlerle test edilir.', icon: <ShieldCheck className="w-5 h-5" /> },
              { n: '02', t: 'Şeffaf Sonuç', d: 'Puan, video ve kriterler doğrudan yayınlanır — filtrelenmez.', icon: <Eye className="w-5 h-5" /> },
              { n: '03', t: 'Bilinçli Karar', d: 'Verileri incelersiniz, satın alma tercihi tamamen sizin.', icon: <CheckCircle2 className="w-5 h-5" /> },
            ].map((s, i) => (
              <div key={i} className="relative border rounded-xl p-6 md:p-7 transition-all hover:-translate-y-0.5 hover:shadow-md" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid={`how-step-${i + 1}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-black tracking-widest" style={{ color: 'var(--text-muted)' }}>{s.n}</span>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--bg-accent-soft)', color: 'var(--green-main)' }} aria-hidden="true">
                    {s.icon}
                  </div>
                </div>
                <h3 className="text-base md:text-lg font-black tracking-tight mb-2" style={{ color: 'var(--text-primary)' }}>{s.t}</h3>
                <p className="text-sm leading-relaxed font-medium" style={{ color: 'var(--text-secondary)' }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="px-5 md:px-6 lg:px-12 py-14 md:py-20" style={{ background: 'linear-gradient(135deg, var(--green-main) 0%, var(--green-light) 100%)' }} data-testid="final-cta">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-2xl mx-auto text-center">
          <p className="text-base md:text-lg font-semibold text-white/90 mb-5">
            Test edilmiş ürünleri inceleyin ve güvenle alışveriş yapın
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/products"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1C3B2A] px-8 py-3.5 text-sm font-bold rounded-lg hover:bg-neutral-100 transition focus-visible:ring-2 focus-visible:ring-white outline-none"
              data-testid="final-cta-primary" tabIndex={0}>
              <CheckCircle2 className="w-4 h-4" />
              Test Edilmiş Ürünleri Gör
            </Link>
          </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
